/**
 * Universal gi-card component scripts (Chef Ri Theme)
 * Handles Wishlist, Variant Selection, Quantity Controls, Card Reveal, and Toast Notifications across all pages.
 */
(function() {
  'use strict';

  // 1. Wishlist Management (localStorage)
  const WISHLIST_KEY = 'cr-wishlist';

  function getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  function syncWishlistUI() {
    const items = getWishlist();
    document.querySelectorAll('.gi-card__wish').forEach(btn => {
      const id = btn.dataset.id;
      if (id && items.includes(id)) {
        btn.classList.add('gi-card__wish--active');
      } else {
        btn.classList.remove('gi-card__wish--active');
      }
    });
  }

  // 2. Global Toast Notification
  window.showToast = function(msg) {
    let toast = document.getElementById('chefriToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'chefriToast';
      toast.className = 'chefri-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = `<span class="chefri-toast-icon">🍪</span><span id="chefriToastMsg"></span>`;
      document.body.appendChild(toast);
    }
    const msgSpan = document.getElementById('chefriToastMsg');
    if (msgSpan) msgSpan.textContent = msg;
    toast.classList.add('show');
    clearTimeout(window._chefriToastTimer);
    window._chefriToastTimer = setTimeout(() => {
      toast.classList.remove('show');
    }, 2800);
  };

  // 3. Event Delegation for Quantity, Wishlist, and Variant Selector
  document.addEventListener('click', function(e) {
    // Quantity button [-] or [+]
    const qtyBtn = e.target.closest('.gi-qty-btn');
    if (qtyBtn) {
      e.preventDefault();
      const form = qtyBtn.closest('form') || qtyBtn.closest('.gi-card__body') || qtyBtn.closest('.gi-card');
      if (!form) return;
      const numSpan = form.querySelector('.gi-qty-num');
      const hiddenInput = form.querySelector('.gi-qty-hidden');
      if (!numSpan || !hiddenInput) return;

      let current = parseInt(hiddenInput.value || numSpan.textContent, 10) || 1;
      if (qtyBtn.dataset.dir === 'up') {
        current = Math.min(current + 1, 99);
      } else {
        current = Math.max(current - 1, 1);
      }
      numSpan.textContent = current;
      hiddenInput.value = current;
      return;
    }

    // Wishlist heart toggle
    const wishBtn = e.target.closest('.gi-card__wish');
    if (wishBtn) {
      e.preventDefault();
      const id = wishBtn.dataset.id;
      if (!id) return;

      const items = getWishlist();
      const index = items.indexOf(id);
      let added = false;
      if (index === -1) {
        items.push(id);
        added = true;
      } else {
        items.splice(index, 1);
      }
      saveWishlist(items);
      syncWishlistUI();

      const card = wishBtn.closest('.gi-card');
      const title = card ? card.dataset.title || 'Item' : 'Item';
      if (added) {
        window.showToast(`${title.charAt(0).toUpperCase() + title.slice(1)} added to your wishlist! ❤️`);
      } else {
        window.showToast(`Removed from your wishlist.`);
      }
      return;
    }
  });

  document.addEventListener('change', function(e) {
    // Variant dropdown selector
    const select = e.target.closest('.gi-variant-select');
    if (select) {
      const option = select.options[select.selectedIndex];
      if (!option) return;

      const card = select.closest('.gi-card');
      if (!card) return;

      // Update price & compare price
      const priceSpan = card.querySelector('[data-price-display]');
      if (priceSpan && option.dataset.price) {
        priceSpan.textContent = option.dataset.price;
      }
      const compareSpan = card.querySelector('.gi-card__compare');
      if (compareSpan) {
        if (option.dataset.compare && option.dataset.compare !== '') {
          compareSpan.textContent = option.dataset.compare;
          compareSpan.style.display = '';
        } else {
          compareSpan.style.display = 'none';
        }
      }

      // Update hidden variant ID input inside form
      const hiddenVariant = card.querySelector('input[ref="variantId"], input[name="id"]');
      if (hiddenVariant) {
        hiddenVariant.value = option.value;
      }

      // Update Add-to-Cart button availability & label
      const atcBtn = card.querySelector('.gi-atc-btn');
      const atcLabel = card.querySelector('.gi-atc-btn__label');
      const isAvailable = option.dataset.avail === 'true';
      if (atcBtn) {
        atcBtn.disabled = !isAvailable;
      }
      if (atcLabel) {
        atcLabel.textContent = isAvailable ? 'Add to Cart' : 'Sold Out';
      }
    }
  });

  // 4. Card Reveal Animation & Initial Sync
  function initCardReveal() {
    const cards = document.querySelectorAll('.gi-card:not(.gi-card--observed)');
    if (!cards.length) return;

    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('gi-card--visible');
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '60px 0px', threshold: 0.05 });

      cards.forEach(card => {
        card.classList.add('gi-card--observed');
        // If already near viewport or inside product grid, add visible immediately or observe
        if (card.getBoundingClientRect().top < window.innerHeight + 100) {
          card.classList.add('gi-card--visible');
        } else {
          observer.observe(card);
        }
      });
    } else {
      cards.forEach(card => {
        card.classList.add('gi-card--observed', 'gi-card--visible');
      });
    }
  }

  // Initialize on DOM ready and observe dynamic DOM changes (infinite scroll / AJAX filtering)
  function init() {
    syncWishlistUI();
    initCardReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for Shopify section/page loads or infinite scroll insertions
  if (typeof MutationObserver !== 'undefined') {
    const domObserver = new MutationObserver((mutations) => {
      let addedGiCards = false;
      for (const m of mutations) {
        if (m.addedNodes.length) {
          for (const node of m.addedNodes) {
            if (node.nodeType === 1 && (node.classList.contains('gi-card') || node.querySelector('.gi-card'))) {
              addedGiCards = true;
              break;
            }
          }
        }
        if (addedGiCards) break;
      }
      if (addedGiCards) {
        syncWishlistUI();
        initCardReveal();
      }
    });
    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

})();
